import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FileCopy from "@mui/icons-material/FileCopy";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfBaseFilter,
  CertificateTemplate,
  IListResponseOfCertificateTemplate,
  ListResponseOfCertificateTemplate,
  ResponseOfCertificateTemplate,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import translateError from "utils/translateError";

interface IListResponseOfCertificateTemplateWithId
  extends IListResponseOfCertificateTemplate {
  id: string;
}

const CertificateTemplateList = () => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const {
    certificateTemplateDetailsUrl,
    certificateTemplateCreateUrl,
    certificateService,
  } = useGlobalContext();

  const { organizationId } = contextStore.useState();

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.certificateTemplateSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const [idToClone, setIdToClone] = useState<string | undefined>();
  const [openCloneCofirmation, openCloneCofirmationActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();

  useListTitle("certificateTemplates");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfCertificateTemplate>(
    [
      "certificateTemplates",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      certificateService.getCertificateTemplates(
        AdminListRequestOfBaseFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  const CloneButton = ({ value }: { value: CertificateTemplate }) => {
    const handleClick = useCallback(
      (e: any) => {
        e.stopPropagation();
        setIdToClone(value?.id);
        openCloneCofirmationActions.setTrue();
      },
      [value]
    );
    return (
      <Box textAlign="center">
        <IconButton
          data-cy="certificateTemplateCloneButton"
          size="small"
          onClick={handleClick}
        >
          <FileCopy />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      type: "text",
      label: t({ id: "certificateTemplateNameLabel" }),
    },
    {
      accessor: "description",
      align: "left",
      type: "text",
      label: t({ id: "certificateTemplateDescriptionLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: CloneButton,
      noSort: true,
      label: t({ id: "certificateTemplateCloneLabel" }),
    },
  ];

  const onCloneConfirm = useCallback(async () => {
    if (idToClone) {
      try {
        var response: ResponseOfCertificateTemplate = await registerPromise(
          certificateService.cloneCertificateTemplate(idToClone)
        );

        queryClient.invalidateQueries("certificateTemplates");

        if (response?.data?.id) {
          navigate(certificateTemplateDetailsUrl(response.data.id));
        }
      } catch (error: any) {
        return { [FORM_ERROR]: translateError(error, t) };
      } finally {
        openCloneCofirmationActions.setFalse();
        setIdToClone(undefined);
      }
    }
  }, [
    idToClone,
    registerPromise,
    certificateService,
    queryClient,
    navigate,
    certificateTemplateDetailsUrl,
    t,
    openCloneCofirmationActions,
  ]);

  const onClick = (id: string) => {
    queryClient.invalidateQueries(["certificateTemplatesOverrides", id]);
    navigate(certificateTemplateDetailsUrl(id));
  };

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const certificateTemplates = data?.data as IListResponseOfCertificateTemplateWithId[];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link
          to={certificateTemplateCreateUrl()}
          style={{ textDecoration: "none" }}
        >
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.certificateTemplateSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.certificateTemplateSearchTerm = e.target.value;
            });
          }}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: certificateTemplates ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "certificateTemplates" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              certificateTemplateDetailsUrl(id)
            ),
          ],
        }}
      />
      <ConfirmDialog
        dialogTitleKey="certificateTemplateCloneLabel"
        contentKey="certificateTemplateCloneComfirmationMessage"
        open={openCloneCofirmation}
        onCancel={openCloneCofirmationActions.setFalse}
        handleConfirm={onCloneConfirm}
      />
    </Container>
  );
};

export default CertificateTemplateList;
